import { useCallback, useContext, useMemo, useEffect, useReducer, useRef, type JSX } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { cx } from '@trinity/chat-components';
import { useRegisterChatPageContext } from '@trinity/chat-components/components/Chat/hooks/useRegisterPageContext.js';
import { DiscoverInsights } from '@trinity/chat-components/components/DiscoverInsights/DiscoverInsights.js';
import { LayoutView } from '@trinity/chat-components/components/LayoutView/index.js';
import { FileLayoutSelector } from '@trinity/chat-components/components/presentation/FileLayoutSelector.js';
import {
  Card,
  CardContent,
  Divider,
  GroupCardHeader,
} from '@trinity/chat-components/components/presentation/Main/index.js';
import { TablePagination } from '@trinity/chat-components/components/presentation/TablePagination.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { TagGeneratingFilesContext } from '@trinity/chat-components/containers/Workspace/context.js';
import {
  CanUploadFilesContext,
  UploadFilesContext,
} from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js';
import {
  useCanUpload,
  useInitiateFilesReview,
} from '@trinity/chat-components/containers/WorkspaceFileUpload/hooks/index.js';
import { useLayoutSelector } from '@trinity/react-utils/hooks/useLayoutSelector';
import { Breadcrumb, Breadcrumbs } from '@/components/presentation/Breadcrumbs';
import { FileUploadReviewContainer } from '@/containers/WorkspaceFileUpload/Upload.Review';
import { WorkspaceBreadcrumbsContext } from '@/containers/WorkspaceObject';
import { WorkspaceObjectType } from '@/enums';
import { ChatContextType } from '@/enums/chat';
import { useFileTagsEvents } from '@/hooks/useFileTagsEvents';
import type { AppLoadedPayload } from '@/store/interfaces';
import { useAppSelector } from '@/store/store';
import type { BrandInsightsPageContext, WorkspaceCapabilities, WorkspaceObjectItem } from '@/types';
import {
  FilesTableDataContext,
  FilesTableLoadingContext,
  FilesTableContext,
  FilesTablePageDataContext,
} from './context';
import { FilesActionsButton } from './Files.Table.Actions';
import { FilesTableEmptyState, FilesTableEmptyStateUpload } from './Files.Table.EmptyState';
import { FilesTableGrid } from './Files.Table.Grid';
import { FilesTableList } from './Files.Table.List';
import * as TableState from './Files.Table.state';
import type { IFilesTable } from './interfaces';
import { SelectedObjectsContainer } from './SelectedObjectsContainer';
import { TagGeneratingFilesProvider } from './TagGeneratingFilesProvider';
import styles from './style/Files.Table.module.css';

type Props = {
  classes?: {
    root?: string;
    pagination?: string;
    table?: string;
    header?: string;
  };
  renderHeaderTitle?: () => JSX.Element;
};

export const FilesTable = ({ classes = {}, renderHeaderTitle }: Props) => {
  const canUpload = useCanUpload();
  const onUpload = useContext(UploadFilesContext);
  const isLoading = useContext(FilesTableLoadingContext);
  const data = useContext(FilesTableDataContext);

  const [layout, setLayout] = useLayoutSelector('sentiment:file-layout', 'list');

  const [tableState, dispatchTable] = useReducer(
    (state: IFilesTable.State<IFilesTable.Item>, action: IFilesTable.Action<IFilesTable.Item>) =>
      TableState.reducer(state, action),
    TableState.getInitialState<IFilesTable.Item>()
  );

  useEffect(() => {
    dispatchTable({ type: 'load', items: data });
  }, [data]);

  const updatePageSize = useCallback(
    (pageSize: number) => dispatchTable({ type: 'update-page-size', pageSize }),
    []
  );
  const nextPage = useCallback(() => dispatchTable({ type: 'next-page' }), []);
  const previousPage = useCallback(() => dispatchTable({ type: 'previous-page' }), []);
  const pageItems = useMemo(
    () => TableState.getPagedItems(tableState.items, tableState.pageIndex, tableState.pageSize),
    [tableState.items, tableState.pageIndex, tableState.pageSize]
  );

  const renderUpload = useCallback(() => {
    return (
      <div className={styles.buttons}>
        <FileLayoutSelector className={styles.selector} selected={layout} onSelect={setLayout} />
        <FilesActionsButton />
      </div>
    );
  }, [layout, setLayout]);

  const Header = useCallback(() => {
    return (
      <GroupCardHeader className={cx(styles.header, classes.header)}>
        <div className={styles.left}>{renderHeaderTitle ? renderHeaderTitle() : <div>Files</div>}</div>
        {renderUpload()}
      </GroupCardHeader>
    );
  }, [classes.header, renderHeaderTitle, renderUpload]);

  const updateSortBy = useCallback((sortBy: IFilesTable.SortingRule) => {
    dispatchTable({ type: 'update-sort-by', sortBy: [sortBy] });
  }, []);

  const footerClasses = useMemo(() => {
    return {
      root: styles.footer,
      pageSize: styles.rows,
      pagination: styles.pagination,
    };
  }, []);

  const renderTableBody = useCallback(() => {
    return (
      <LayoutView
        layout={layout}
        listContent={
          <FilesTableList
            classes={classes}
            items={pageItems}
            canUpload={canUpload}
            onUpload={onUpload}
            pagination={{
              pageIndex: tableState.pageIndex,
              pageSize: tableState.pageSize,
              sortBy: tableState.sortBy,
              updateSortBy,
            }}
          />
        }
        gridContent={
          <FilesTableGrid classes={classes} items={pageItems} canUpload={canUpload} onUpload={onUpload} />
        }
      />
    );
  }, [
    layout,
    classes,
    pageItems,
    canUpload,
    onUpload,
    tableState.pageIndex,
    tableState.pageSize,
    tableState.sortBy,
    updateSortBy,
  ]);

  const renderContent = useCallback(() => {
    if (isLoading) return null;

    if (!data?.length) {
      if (canUpload) {
        return <FilesTableEmptyStateUpload />;
      } else {
        return <FilesTableEmptyState />;
      }
    }

    return (
      <>
        {renderTableBody()}
        {!!data?.length && (
          <FilesTableFooter
            classes={footerClasses}
            pageSize={tableState.pageSize}
            pageIndex={tableState.pageIndex}
            totalCount={tableState.totalCount}
            canNextPage={tableState.canNextPage}
            canPreviousPage={tableState.canPreviousPage}
            updatePageSize={updatePageSize}
            nextPage={nextPage}
            previousPage={previousPage}
          />
        )}
      </>
    );
  }, [
    isLoading,
    data?.length,
    renderTableBody,
    footerClasses,
    tableState.pageSize,
    tableState.pageIndex,
    tableState.totalCount,
    tableState.canNextPage,
    tableState.canPreviousPage,
    updatePageSize,
    nextPage,
    previousPage,
    canUpload,
  ]);

  return (
    <FilesTablePageDataContext.Provider value={pageItems}>
      <Card className={cx(styles.card, classes.table)}>
        <Header />
        {!data?.length && <Divider />}
        <CardContent className={styles.content} fluid>
          {renderContent()}
        </CardContent>
      </Card>
    </FilesTablePageDataContext.Provider>
  );
};

type FilesTableFooterProps = {
  classes?: {
    root?: string;
    pageSize?: string;
    pagination?: string;
  };
  pageSize: number;
  pageIndex: number;
  totalCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  updatePageSize: (size: number) => unknown;
  nextPage: () => unknown;
  previousPage: () => unknown;
};

const FilesTableFooter = ({
  pageSize,
  pageIndex,
  totalCount,
  nextPage,
  previousPage,
  canNextPage,
  canPreviousPage,
  classes = {},
}: FilesTableFooterProps) => {
  return (
    <div className={cx(styles.paginationRoot, classes.root)}>
      <TablePagination
        className={cx(styles.pagination, classes.pagination)}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageIndex={pageIndex}
        nextPage={nextPage}
        pageSize={pageSize}
        previousPage={previousPage}
        totalCount={totalCount}
      />
    </div>
  );
};

const WorkspaceFilesTableContent = () => {
  const { entity, object } = useSelector((state: AppLoadedPayload) => state.workspace.group);
  const workspaceBrandsEnabled = useAppSelector((state) => state.features.workspaceBrands);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: [`get:workspaces/objects`, entity.id, object.id],
    queryFn: () => {
      return apiClient.workspaces
        .fetchWorkspaceFiles({
          workspaceId: entity.id,
          objectId: object.id,
        })
        .then((res) => ({
          ...res,
          items: res.items.filter((f) =>
            workspaceBrandsEnabled ? true : f.object.typeId !== WorkspaceObjectType.Brand
          ),
        }));
    },
  });

  const tagGeneratingContext = useContext(TagGeneratingFilesContext);

  // Get list of files currently generating tags
  const generatingFileIds = useMemo(() => {
    if (!tagGeneratingContext?.generatingFileIds) return [];
    return Array.from(tagGeneratingContext.generatingFileIds);
  }, [tagGeneratingContext?.generatingFileIds]);

  // Stringify generating file IDs for reliable dependency tracking
  const generatingFileIdsKey = useMemo(() => {
    if (generatingFileIds.length === 0) return '';
    return generatingFileIds
      .slice()
      .sort((a, b) => a - b)
      .join(',');
  }, [generatingFileIds]);

  // Subscribe to WebSocket events for generating files
  // Events will automatically trigger query invalidation
  useFileTagsEvents(generatingFileIds);

  // Remove files from generating state when tags appear in the data
  // This is a fallback in case WebSocket events don't fire or are missed
  // Also handles cleanup on page refresh - if files in generating state already have tags, remove them
  useEffect(() => {
    if (!tagGeneratingContext || !query.data?.items) return;

    const generatingIds = Array.from(tagGeneratingContext.generatingFileIds);
    if (generatingIds.length === 0) return;

    const items = query.data.items;
    items.forEach((item) => {
      const isFileItem = 'file' in item && item.file !== null;
      if (isFileItem && item.file) {
        const fileId = item.file.id;
        if (tagGeneratingContext.isGenerating(fileId) && item.tags && item.tags.length > 0) {
          tagGeneratingContext.removeGeneratingFile(fileId);
        }
      }
    });
  }, [query.data?.items, tagGeneratingContext, generatingFileIdsKey]);

  const chatContext: BrandInsightsPageContext.Global = useMemo(() => {
    return {
      type: ChatContextType.Global,
    };
  }, []);

  const register = useRegisterChatPageContext();

  useEffect(() => {
    if (!chatContext) return;

    register(chatContext);
  }, [register, chatContext]);

  const uploadFiles = useInitiateFilesReview({
    parentObjectId: object.id,
    workspaceId: entity.id,
  });

  return (
    <SelectedObjectsContainer>
      <FilesTableDataContext.Provider value={(query.data?.items as WorkspaceObjectItem[]) || []}>
        <FilesTableLoadingContext.Provider value={query.isLoading}>
          <WorkspaceBreadcrumbsContext.Provider value={query.data?.breadcrumbs || []}>
            <FilesTableContext.Provider value={{ parentObject: { id: object.id, workspaceId: entity.id } }}>
              <UploadFilesContext.Provider value={uploadFiles}>
                <CanUploadFilesContext.Provider
                  value={(query.data?.capabilities as WorkspaceCapabilities)?.canAddChildren}
                >
                  <div className={styles.root}>
                    <div className={styles.wrap}>
                      <div className={styles.banner}>
                        <div className={styles.main}>
                          <div className={styles.h2}>
                            <Breadcrumbs>
                              <Breadcrumb>Workspace</Breadcrumb>
                            </Breadcrumbs>
                            <DiscoverInsights />
                          </div>
                        </div>
                      </div>
                      <FilesTable />
                    </div>
                  </div>
                </CanUploadFilesContext.Provider>
              </UploadFilesContext.Provider>
            </FilesTableContext.Provider>
          </WorkspaceBreadcrumbsContext.Provider>
        </FilesTableLoadingContext.Provider>
      </FilesTableDataContext.Provider>
    </SelectedObjectsContainer>
  );
};

export const WorkspaceFilesTable = () => {
  return (
    <TagGeneratingFilesProvider>
      <FileUploadReviewContainer>
        <WorkspaceFilesTableContent />
      </FileUploadReviewContainer>
    </TagGeneratingFilesProvider>
  );
};
